import React from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, useLocation } from 'react-router-dom';

/**
 * Component to handle protected routes
 * Redirects to login if user is not authenticated
 * Supports role-based access control (admin, donor, recipient)
 */
function ProtectedRoute({ 
  children, 
  requiredRole = null, // Can be 'admin', 'donor', 'recipient' or null (any authenticated user)
  redirectPath = '/dashboard'
}) {
  const { user, loading, isAdmin, isDonor, isRecipient } = useAuth();
  const location = useLocation();

  // If still loading auth state, show loading spinner
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  // If user is not logged in, redirect to login
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Handle role-based access control
  if (requiredRole) {
    let hasAccess = false;
    
    switch(requiredRole) {
      case 'admin':
        hasAccess = isAdmin();
        break;
      case 'donor':
        hasAccess = isDonor() || isAdmin(); // Admins can access donor routes
        break;
      case 'recipient':
        hasAccess = isRecipient() || isAdmin(); // Admins can access recipient routes
        break;
      default:
        hasAccess = true;
    }
    
    if (!hasAccess) {
      return <Navigate to={redirectPath} replace />;
    }
  }

  // If user is authenticated (and is admin if required), render the children
  return children;
}

export default ProtectedRoute;
